// ─── Hog of War V2 UI Design Tokens ─────────────────────────────────────────
// Source of truth: UI_SPEC.md
// Reference implementation: public/battle-mockupV4.html

import { BATTLEFIELD_CAPACITIES, DISPLAY_DEPTH } from '../rules/gameConfig'

// ── Layout ────────────────────────────────────────────────────────────────────
const RADIAL_BTN_SIZE = 52

export const LAYOUT = {
  SCREEN_W:          375,
  SCREEN_H:          'dynamic',  // 100svh — accounts for mobile browser chrome
  HEADER_H:          50,         // 37.5px kill slots + 6px padding each side
  SUBMIT_H:          44,
  HOME_BAR_H:        0,          // hidden in browser context
  SUBMIT_PADDING_T:  10,
  GRID_COLS:         BATTLEFIELD_CAPACITIES.length,   // = BATTLEFIELD_COUNT
  GRID_ROWS:         DISPLAY_DEPTH * 2,               // opp rows + player rows
  COL_GAP:           3,
  ROW_GAP:           1,
  DIVIDER_H:         3,
  COL_NUM_SIZE:      20,
  RADIAL_BTN_SIZE,
  RADIAL_BTN_RADIUS: RADIAL_BTN_SIZE / 2,
  MICRO_DOT_SIZE:    4,
  MICRO_DOT_OFFSET:  13,
}

// ── Chrome / Background ───────────────────────────────────────────────────────
export const COLOR = {
  BG:           '#111',
  BORDER_DARK:  '#2a2a2a',

  // Territory cells
  CELL_OPP:    '#7a3040',
  CELL_PLAYER: '#8c7a50',
  CELL_INACC:  '#372e24',

  // Scoreboard
  SCORE_YOU_BG:  'rgba(212,134,10,0.15)',
  SCORE_OPP_BG:  'rgba(192,64,64,0.15)',
  SCORE_YOU_TAG: '#D4860A',
  SCORE_YOU_NUM: '#E8980E',
  SCORE_OPP_TAG: '#C04040',
  SCORE_OPP_NUM: '#D45050',
  PAIRING_DOT:   '#3fb950',

  // Army blocks — flat fills. New/incoming units wear YELLOW — the same
  // "not available this round" family as the pending dial and moved stripes
  // (green is reserved for movable: dial, move-ready stroke, MOVE button).
  ARMY_PLAYER:     '#D4860A',
  ARMY_PLAYER_NEW: '#f0e040',
  ARMY_OPP:          '#C04040',
  // Opp resupply tile is a red→green diagonal gradient (ARMY_OPP → GRAD_NEW),
  // defined in ArmyBlock.css (.ab__block--opp-resupply) — a flat mix reads as olive muck.

  // Lead tile gradient stops
  GRAD_AMBER_LIGHT: '#D4860A',  // bright tip (normal)
  GRAD_AMBER_DARK:  '#8C6228',  // dark base (normal)
  GRAD_RED:         '#C04040',  // attacking gradient start
  GRAD_NEW:         '#f0e040',  // newly deployed / in-construction flat fill (yellow = can't act yet)

  // Your own structures on the Grid — the ring, the wash, the kill-bar icon
  // and the silhouette that flies to it. TEAL, the one hue nothing else on
  // the board spends: it keeps your buildings apart from the yellow
  // "can't act yet" family (new units, drop zones, construction) and the
  // amber unit family. Enemy structures have no colour of their own on the
  // board (their Grid is secret); their kill-bar icons wear the opp red.
  STRUCT_SELF:       '#2ec4b6',
  STRUCT_SELF_DARK:  '#1f9c90',  // the silhouette gradient's far stop
  STRUCT_SELF_RGB:   '46, 196, 182',

  // Base maturation loading bar (above the parachute on in-construction tiles).
  // Green fill while building; gone once the Base matures.
  BASE_PROGRESS:      '#5abf6e',

  // Army icons
  ICON_LEAD_NORMAL:     '#f0e040',           // yellow, full opacity — planned
  ICON_LEAD_DIMMED:     'rgba(240,224,64,0.5)', // yellow, 50% — moved / planChanged
  ICON_PLAYER_ATTACK:   '#FFAAAA',
  ICON_OPP:             'rgba(255,255,255,0.7)',
  ICON_RESERVE:         '#ffffff',
  ICON_RESERVE_NEW:     'rgba(255,255,255,0.5)',

  // Lead army pointy-edge stroke
  LEAD_STROKE:          '#f0e040',           // same yellow as BTN_MOVE

  // Radial menu
  RADIAL_BG:          '#070f0f',
  RADIAL_BORDER:      'rgba(255,255,255,0.18)',
  RADIAL_COL_OVERLAY: 'rgba(0,0,0,0.38)',
  BTN_ATTACK:         '#e05050',
  BTN_MOVE:           '#f0e040',   // undo-move buttons/dots (yellow = the spent-move family)
  BTN_MOVE_READY:     '#5abf6e',   // MOVE button — green = movable, matching the settled dial + move-ready stroke (brightened for the near-black radial button, like ROCKET_BTN)
  BTN_PLAN:           '#a8c8dc',
  BTN_FORT:           '#d4f0ee',
  ROCKET:             '#1c4f8f',  // dark blue — the rocket tile/frame border (ROCKET_COLOR in HexBoard.js)
  ROCKET_BTN:         '#3f86d6',  // brightened so it reads as the SAME blue on the near-black radial button

  // Drop zones / resupply
  DROP_ZONE:        '#f0e040',               // pending drop-zone bullseye + placement plus (yellow = incoming, can't act yet)

  // Divider / column numbers
  DIVIDER:      '#f0e040',
  COL_NUM_BG:   '#f0e040',
  COL_NUM_TEXT: '#111',

  // Submit
  SUBMIT_BG:     '#D4860A',
  SUBMIT_BORDER: '#E8980E',
  SUBMIT_TEXT:   '#111',
  SUBMIT_ACTIVE: '#bf780a',
}

// ── Grid cell type lookup ─────────────────────────────────────────────────────
// v2: all 35 plots are always in play — warCapacity no longer gates targeting.
// Opp half   (rows 0 – DISPLAY_DEPTH-1): always 'opp'
// Player half (rows DISPLAY_DEPTH – GRID_ROWS-1): always 'player'
export function buildGridLayout() {
  return Array.from({ length: LAYOUT.GRID_ROWS }, (_, r) =>
    Array.from({ length: LAYOUT.GRID_COLS }, () =>
      r < DISPLAY_DEPTH ? 'opp' : 'player'
    )
  )
}

// Returns which side the Undo Move button should appear on.
// gameLogic.js stores movedFrom as 'left'|'right' — the direction the army came from —
// which is exactly the side the undo button should point toward.
export const undoMoveDirection = (movedFrom) => {
  return movedFrom ?? null
}

// ── Army state derivation ─────────────────────────────────────────────────────
// Returns one of:
//   'attacking' | 'fortAttacking' |
//   'moved' |
//   'fortInConstruction' |
//   'fort' |
//   'newlyDeployed' | 'movedOut' | 'ready' | 'empty'
export const getLeadArmyState = (bf) => {
  const hasArmies = bf.armyQty > 0 || bf.fortCount > 0 || bf.fortInConstruction > 0 ||
                    bf.newArmies > 0 || bf.movedInArmies > 0
  if (!hasArmies) return 'empty'
  if (bf.isAttacking) return (bf.fortCount > 0 && bf.armyQty === 0) ? 'fortAttacking' : 'attacking'
  // Moved-in is the lead when no pre-existing armies are present (forts are immobile reserves)
  if (bf.movedInArmies > 0 && bf.armyQty === 0) return 'moved'
  // Fort in construction holds the lead over newly deployed and mature forts
  if (bf.fortInConstruction > 0) return 'fortInConstruction'
  // Mature fort is lead when no pre-existing armies are present (armyQty === 0).
  // Forts are permanently immobile — no move action.
  if (bf.fortCount > 0 && bf.armyQty === 0) return 'fort'
  // Newly deployed is the lead when there are no pre-existing (armyQty) armies
  if (bf.newArmies > 0 && bf.armyQty === 0) return 'newlyDeployed'
  // An army already moved out this turn — remaining army cannot move again
  if (bf.movedOut) return 'movedOut'
  return 'ready'
}

// Returns true when a reserve square should be shown behind the lead.
export const hasReserve = (bf) => {
  // Lead priority mirrors getLeadArmyState
  const leadIsMovedIn  = bf.movedInArmies > 0 && bf.armyQty === 0
  const leadIsTraining = bf.fortInConstruction > 0 && !leadIsMovedIn
  const leadIsFort     = bf.fortCount > 0 && bf.armyQty === 0 && !leadIsMovedIn && !leadIsTraining
  const leadIsNew      = bf.newArmies > 0 && bf.armyQty === 0 && !leadIsMovedIn && !leadIsTraining && !leadIsFort

  const backingArmies = leadIsMovedIn
    // Moved-in is lead; remaining moved-in + forts + newly deployed are reserve
    ? bf.movedInArmies - 1 + bf.fortCount + bf.fortInConstruction + bf.newArmies
    : leadIsTraining
      // Fort in construction is lead; all other armies/forts are reserve
      ? bf.armyQty + bf.fortCount + bf.newArmies + bf.movedInArmies
    : leadIsFort
      // Mature fort is lead (armyQty already 0); new/moved-in armies are reserve
      ? bf.newArmies + bf.movedInArmies
    : leadIsNew
      // New army is lead; rest of new + moved-in + forts are reserve
      ? bf.armyQty + bf.fortCount + (bf.newArmies - 1) + bf.movedInArmies
      // Existing army is lead; newArmies/forts show as reserve behind it
      : (bf.armyQty - 1) + bf.fortCount + bf.movedInArmies + bf.newArmies
  return backingArmies > 0
}
